import { ReactNode } from 'react';
import PasswordResetErrorBanner from '../components/password-reset/PasswordResetErrorBanner';
import PasswordRequirementsV2 from '../components/password-reset/PasswordRequirementsV2';
import { createWrapper, RenderReactComponentArgs } from './react-utils';

interface RenderForgeAndHideOktaArgs {
  id: string;
  oktaComponent: JQuery;
  forgeComponent: ReactNode;
}

/**
 * Shared logic for rendering PasswordResetErrorBanner in place of the default Okta error container.
 * Observes both password and confirm password input containers for error mapping.
 */
export function forgifyPasswordResetErrorContainer(
  renderForgeAndHideOktaComponent: (args: RenderForgeAndHideOktaArgs) => void
) {
  const errorContainers: Readonly<JQuery> = jQueryCourage('div[data-se="o-form-error-container"]');
  if (errorContainers.length) {
    const passwordFieldset = jQueryCourage('[data-se="o-form-fieldset-credentials.passcode"]');
    const passwordInputContainer = passwordFieldset.find('[data-se="o-form-input-container"]');
    const confirmFieldset = passwordFieldset.siblings('[data-se="o-form-fieldset"]');
    const confirmInputContainer = confirmFieldset.find('[data-se="o-form-input-container"]');
    const passwordInputContainers = [ passwordInputContainer, confirmInputContainer ];

    errorContainers.each((index: number) => {
      const errorContainer = errorContainers.eq(index);
      const forgeComponent = (
        <PasswordResetErrorBanner
          oktaErrorContainer={errorContainer}
          passwordInputContainers={passwordInputContainers}
        />
      );
      renderForgeAndHideOktaComponent({
        id: `${errorContainer.attr('class')}${index}`,
        oktaComponent: errorContainer,
        forgeComponent: forgeComponent,
      });
    });
  }
}

/**
 * Shared logic for rendering PasswordRequirementsV2 and suppressing inline errors
 * on both password and confirm password input fields.
 */
export function forgifyPasswordResetRequirements(
  containerSelector: string,
  renderReactComponent: (args: RenderReactComponentArgs) => void,
  addToHideList: (element: HTMLElement) => void
) {
  const passwordRequirements: JQuery = jQueryCourage(containerSelector);

  const component = (
    <PasswordRequirementsV2 />
  );

  // Create wrapper and insert it after the Forge password input field
  const wrapper = createWrapper({ hidden: false });
  jQueryCourage(wrapper).addClass('password-requirements-v2-wrapper');
  const forgePasswordInputField = jQueryCourage('#athena-credentials\\.passcode-input-field-container');

  if (forgePasswordInputField.length) {
    forgePasswordInputField.addClass('password-reset-suppress-input-error');
    forgePasswordInputField.after(wrapper);
  }

  // Suppress the error display on the confirm password input field
  const forgeConfirmPasswordField = jQueryCourage('#athena-confirmPassword-input-field-container');
  if (forgeConfirmPasswordField.length) {
    forgeConfirmPasswordField.addClass('password-reset-suppress-input-error');
  }

  renderReactComponent({
    id: passwordRequirements.attr('data-se')!,
    component: component,
    container: wrapper,
  });

  addToHideList(passwordRequirements[0]);
}
